import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import Search from './search/Search';

const FilterBar = (props) => {
  const {
    selection,
    updateFilter,
    onClearFilter,
    filterOptions,
  } = props;

  const searchValue = selection?.filter?.searchString?.optionSelected ?? '';
  const statusFilter = filterOptions?.status;
  const selectedStatus = selection?.filter?.status?.optionSelected ?? 'ACTIVE';
  const statusMap = translate('client.Status');
  const options = React.useMemo(() => {
    return Object.values(statusFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [statusFilter]);
  const isFilterDirty = Boolean(searchValue.trim()) || selectedStatus !== 'ACTIVE';

  if (!statusFilter) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          alignContent: 'flex-start',
          width: '100%',
          gap: '12px 8px',
        }}
      >
        <Search
          selection={selection}
          filterOptions={filterOptions}
          updateFilter={updateFilter}
        />
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        <Typography
          sx={{
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '14px',
            color: '#6B6760',
            whiteSpace: 'nowrap',
          }}
        >
          {translate('client.ClientList.Filter.Status')}
        </Typography>
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        {
          options.map((option) => {
            const isSelected = option.key === selectedStatus;
            const optionColors = statusMap[option.key] ?? {};
            const backgroundColor = isSelected ? (optionColors.Color ?? '#FFF7F4') : '#F9F8F6';
            const borderColor = isSelected ? (optionColors.ColorBorder ?? optionColors.Color ?? '#EBBDAD') : '#E8E4DE';
            const textColor = isSelected ? (optionColors.ColorText ?? '#DA7955') : '#6B6760';

            return (
              <ButtonBase
                key={option.key}
                onClick={() => updateFilter(statusFilter, option)}
                sx={{
                  'display': 'flex',
                  'alignItems': 'center',
                  'justifyContent': 'center',
                  'px': '9px',
                  'py': '4px',
                  'borderRadius': '20px',
                  'border': `1px solid ${borderColor}`,
                  backgroundColor,
                  'whiteSpace': 'nowrap',
                  'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Geist Mono',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '14px',
                    color: textColor,
                  }}
                >
                  {option.label}
                </Typography>
              </ButtonBase>
            );
          })
        }
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        <ButtonBase
          onClick={onClearFilter}
          sx={{
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'px': '9px',
            'py': '4px',
            'borderRadius': '20px',
            'border': '1px solid #E8E4DE',
            'backgroundColor': isFilterDirty ? '#FFF7F4' : '#F9F8F6',
            'whiteSpace': 'nowrap',
            'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
            },
          }}
        >
          <Typography
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '14px',
              color: isFilterDirty ? '#DA7955' : '#6B6760',
            }}
          >
            <Translate expression='generic.ClearAllFilters' />
          </Typography>
        </ButtonBase>
      </Box>
    </Box>
  );
};

FilterBar.propTypes = {
  selection: PropTypes.object.isRequired,
  filterOptions: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
  onClearFilter: PropTypes.func.isRequired,
};

export default React.memo(FilterBar);
